import React, { useEffect, useState } from 'react';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { t } from '../../i18n';
import { AppFooter } from '../Common/AppFooter';
import SEO from '../Common/SEO';

interface PrivacySection {
    title: string;
    content: string | React.ReactElement;
    items?: string[];
}

interface PrivacyViewProps {
    onBack: () => void;
    initialTab?: 'privacy' | 'terms';
}

export const PrivacyView: React.FC<PrivacyViewProps> = ({ onBack, initialTab }) => {
    const { lang } = useLanguage();
    const [isAnimating, setIsAnimating] = useState(false);
    const [activeTab, setActiveTab] = useState<'privacy' | 'terms'>(initialTab || 'privacy');

    useEffect(() => {
        if (initialTab) {
            setActiveTab(initialTab);
        }
    }, [initialTab]);

    useEffect(() => {
        setIsAnimating(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const scrollContainers = document.querySelectorAll('.overflow-y-auto');
        scrollContainers.forEach(container => {
            container.scrollTop = 0;
        });
    }, [activeTab]);

    const privacySections: PrivacySection[] = [
        {
            title: t(lang, 'privacySection1Title'),
            content: t(lang, 'privacySection1Desc'),
            items: [
                t(lang, 'privacyItemIP'),
                t(lang, 'privacyItemBrowser'),
                t(lang, 'privacyItemDate'),
                t(lang, 'privacyItemPage'),
                t(lang, 'privacyItemCookie')
            ]
        },
        {
            title: t(lang, 'privacySection2Title'),
            content: t(lang, 'privacySection2Desc'),
            items: [
                t(lang, 'privacyUsageStats'),
                t(lang, 'privacyUsageImprove'),
                t(lang, 'privacyUsageAds')
            ]
        },
        {
            title: t(lang, 'privacySection3Title'),
            content: (
                <div className="space-y-4">
                    <p>{t(lang, 'privacySection3Desc1')}</p>
                    <p>{t(lang, 'privacySection3Desc2')}</p>
                </div>
            )
        },
        {
            title: t(lang, 'privacySection4Title'),
            content: (
                <div>
                    <p>{t(lang, 'privacySection4Desc1')}</p>
                    <p>{t(lang, 'privacySection4Desc2')}</p>
                    <p>{t(lang, 'privacySection4Desc3')}</p>
                    <p>{t(lang, 'privacySection4Desc4')}</p>
                </div>
            )
        },
        {
            title: t(lang, 'privacySection5Title'),
            content: (
                <div className="space-y-4">
                    <p>{t(lang, 'privacySection5Desc1')}</p>
                    <p>{t(lang, 'privacySection5Desc2')}</p>
                    <p>{t(lang, 'privacySection5Desc3')}</p>
                </div>
            )
        }
    ];

    const termsSections: PrivacySection[] = [
        {
            title: t(lang, 'termsSection1Title'),
            content: t(lang, 'termsSection1Desc')
        },
        {
            title: t(lang, 'termsSection2Title'),
            content: t(lang, 'termsSection2Desc')
        },
        {
            title: t(lang, 'termsSection3Title'),
            content: t(lang, 'termsSection3Desc')
        },
        {
            title: t(lang, 'termsSection4Title'),
            content: t(lang, 'termsSection4Desc')
        },
        {
            title: t(lang, 'termsSection5Title'),
            content: t(lang, 'termsSection5Desc')
        },
        {
            title: t(lang, 'termsSection6Title'),
            content: t(lang, 'termsSection6Desc')
        }
    ];

    return (
        <div className="min-h-screen flex flex-col bg-white">
            <SEO 
                title={activeTab === 'privacy' ? t(lang, 'privacyPolicy') : t(lang, 'termsOfService')}
                description={t(lang, 'privacyDesc1')}
            />
            
            <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 px-6 py-4 flex items-center gap-4">
                <button onClick={onBack} className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full text-slate-600 active:scale-90 transition-all md:hover:bg-rose-50 md:hover:text-rose-500">
                    <ArrowLeft size={20} />
                </button>
                <h1 className="text-xl font-black text-slate-900 tracking-tight uppercase">
                    {activeTab === 'privacy' ? t(lang, 'privacyPolicy') : t(lang, 'termsOfService')}
                </h1>
            </header>

            <main className="flex-1 overflow-y-auto custom-scrollbar">
                <div className={`max-w-3xl mx-auto pb-12 pt-8 px-6 ${isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} transition-all duration-500`}>
                    
                    {/* Tab Selection */}
                    <div className="flex p-1 bg-slate-100 rounded-2xl mb-12">
                        <button 
                            onClick={() => setActiveTab('privacy')}
                            className={`flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                                activeTab === 'privacy' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400'
                            }`}
                        >
                            {t(lang, 'privacyPolicy')}
                        </button>
                        <button 
                            onClick={() => setActiveTab('terms')}
                            className={`flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                                activeTab === 'terms' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400'
                            }`}
                        >
                            {t(lang, 'termsOfService')}
                        </button>
                    </div>

                    <div className="space-y-12">
                        {(activeTab === 'privacy' ? privacySections : termsSections).map((section, idx) => (
                            <section key={idx} className="space-y-6">
                                <div className="flex items-center gap-3">
                                    <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">
                                        {section.title}
                                    </h3>
                                </div>
                                <div className="text-slate-600 leading-relaxed font-medium whitespace-pre-line pl-11">
                                    {typeof section.content === 'string' ? <p>{section.content}</p> : section.content}
                                    {section.items && (
                                        <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {section.items.map((item, i) => (
                                                <li key={i} className="flex items-center gap-2 text-sm">
                                                    <CheckCircle2 size={14} className="text-rose-500" />
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </section>
                        ))}
                    </div>

                </div>
                <AppFooter lang={lang} />
            </main>
        </div>
    );
};
